import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { deliveriesKey } from './TaskDeliveries';
import { TASKS_KEY } from './types';

export type ReminderChannel = 'email' | 'sms' | 'push';
export type Reminder = { id: string; remindAt: string; channels: ReminderChannel[] };
/** POST /api/company-tasks/:id/reminders: `pushSent` only for "Przypomnij teraz". */
export type AddedReminder = Reminder & { pushSent?: boolean };
/** GET /api/company-tasks/:id/reminders */
export type Reminders = { reminders: Reminder[]; channels: Record<ReminderChannel, boolean>; nowAvailableAt: string | null };
export type WhenKind = 'hour' | 'tomorrow' | 'day_before' | 'custom';

export const WHEN_LABELS: Record<WhenKind, string> = { hour: 'Za godzinę', tomorrow: 'Jutro o 9:00', day_before: 'Dzień przed terminem, o 9:00', custom: 'Własna data i godzina' };
export const CHANNEL_LABELS: Record<ReminderChannel, string> = { email: 'E-mail', sms: 'SMS', push: 'Push (telefon, komputer)' };
const CHANNEL_SHORT: Record<ReminderChannel, string> = { email: 'e-mail', sms: 'SMS', push: 'push' };

const AT = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', weekday: 'short', day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
/** "pt., 8.11.2030, 18:30 · dzwonek, push" (Warsaw time). */
export function reminderLabel(r: Reminder): string {
  return `${AT.format(new Date(r.remindAt))} · ${['dzwonek', ...r.channels.map((c) => CHANNEL_SHORT[c])].join(', ')}`;
}

/** Minutes until "Przypomnij teraz" works again (at least 1), or 0. */
export function minutesLeft(availableAt: string | null, now = Date.now()): number {
  if (!availableAt) return 0;
  const ms = new Date(availableAt).getTime() - now;
  return ms > 0 ? Math.max(1, Math.ceil(ms / 60_000)) : 0;
}

/** After "Przypomnij teraz": push chosen but no device of the person got it — say so (the server answers only yes/no). */
export function sentNote(chosen: readonly ReminderChannel[], result: { pushSent?: boolean }): string {
  return chosen.includes('push') && result.pushSent === false
    ? 'Przypomnienie wysłane (bez push — osoba nie ma włączonych powiadomień na urządzeniu).'
    : 'Przypomnienie wysłane.';
}

/**
 * "Przypomnienia" in the task dialog (owner remarks 4, 2026-10-22 00:15–00:16): "Przypomnij teraz", "Przypomnij…" at
 * a chosen time, and the channels besides the bell. Only for whoever may edit the task; the server checks again.
 */
export function TaskReminders({ taskId, dueDayVisible }: { taskId: string; dueDayVisible: boolean }) {
  const queryClient = useQueryClient();
  const key = [...TASKS_KEY, 'reminders', taskId];
  const list = useQuery({ queryKey: key, queryFn: () => api<Reminders>(`/api/company-tasks/${taskId}/reminders`) });
  const [channels, setChannels] = useState<ReminderChannel[]>(['push']);
  const [planning, setPlanning] = useState(false);
  const [when, setWhen] = useState<WhenKind>('tomorrow');
  const [day, setDay] = useState('');
  const [time, setTime] = useState('09:00');
  const [sentText, setSentText] = useState<string | null>(null);
  // a reminder sent now is a new entry in "Powiadomienia" too
  const refresh = () => Promise.all([queryClient.invalidateQueries({ queryKey: key }), queryClient.invalidateQueries({ queryKey: deliveriesKey(taskId) })]);

  const available = list.data?.channels ?? { email: false, sms: false, push: false };
  const chosen = channels.filter((c) => available[c]);
  const add = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<AddedReminder>(`/api/company-tasks/${taskId}/reminders`, { method: 'POST', body: { ...body, channels: chosen } }),
    onSuccess: (r, body) => {
      if (body.when === 'now') setSentText(sentNote(r.channels, r));
      else setPlanning(false);
    },
    onSettled: refresh,
  });
  const remove = useMutation({ mutationFn: (id: string) => api(`/api/company-tasks/${taskId}/reminders/${id}`, { method: 'DELETE' }), onSettled: refresh });

  const wait = minutesLeft(list.data?.nowAvailableAt ?? null);
  const error = add.error ?? remove.error;
  const issues = error instanceof ApiError ? fieldIssues(error) : [];
  const whens = (Object.keys(WHEN_LABELS) as WhenKind[]).filter((w) => w !== 'day_before' || dueDayVisible);

  return (
    <section
      className="ct-reminders"
      aria-labelledby={`ct-rem-${taskId}`}
      data-feature-id="company-tasks.reminders"
      // inside the task's form: Enter in the day or time must not save and close the task
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target instanceof HTMLInputElement) e.preventDefault();
      }}
    >
      <h3 id={`ct-rem-${taskId}`}>Przypomnienia</h3>
      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.data && (
        <>
          <fieldset className="ct-rem-channels">
            <legend>Gdzie przypomnieć</legend>
            <label className="ct-check">
              <input type="checkbox" checked disabled /> Dzwonek
            </label>
            {(Object.keys(CHANNEL_LABELS) as ReminderChannel[])
              .filter((c) => available[c])
              .map((c) => (
                <label key={c} className="ct-check">
                  <input type="checkbox" checked={channels.includes(c)} onChange={(e) => setChannels((cs) => (e.target.checked ? [...cs, c] : cs.filter((x) => x !== c)))} /> {CHANNEL_LABELS[c]}
                </label>
              ))}
          </fieldset>
          <div className="ct-rem-actions">
            <button type="button" className="btn small" disabled={add.isPending || wait > 0} onClick={() => add.mutate({ when: 'now' })}>
              Przypomnij teraz
            </button>
            <button type="button" className="btn small ghost" aria-expanded={planning} onClick={() => setPlanning((v) => !v)}>
              Przypomnij…
            </button>
            {wait > 0 && <span className="muted">{sentText ?? 'Przypomnienie wysłane.'} Kolejne „Przypomnij teraz” za {wait} min.</span>}
          </div>
          {planning && (
            <div className="ct-rem-plan" role="group" aria-label="Zaplanuj przypomnienie">
              <label className="field">
                <span>Kiedy</span>
                <select value={when} onChange={(e) => setWhen(e.target.value as WhenKind)}>
                  {whens.map((w) => (
                    <option key={w} value={w}>
                      {WHEN_LABELS[w]}
                    </option>
                  ))}
                </select>
              </label>
              {when === 'custom' && (
                <div className="ct-row">
                  <label className="field">
                    <span>Dzień</span>
                    <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
                  </label>
                  <label className="field">
                    <span>Godzina</span>
                    <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
                  </label>
                </div>
              )}
              <button type="button" className="btn small primary" disabled={add.isPending} onClick={() => add.mutate({ when, ...(when === 'custom' ? { day, time } : {}) })}>
                Dodaj przypomnienie
              </button>
            </div>
          )}
          {list.data.reminders.length > 0 && (
            <ul className="ct-rem-list" aria-label="Zaplanowane przypomnienia">
              {list.data.reminders.map((r) => (
                <li key={r.id}>
                  <span>{reminderLabel(r)}</span>
                  <button type="button" className="btn ghost small" disabled={remove.isPending} onClick={() => remove.mutate(r.id)} aria-label={`Usuń przypomnienie ${reminderLabel(r)}`}>
                    Usuń
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {error && (
        <p className="alert" role="alert">
          {issues.length > 0 ? issues.map((i) => i.message).join(' ') : error instanceof ApiError ? error.message : 'Nie udało się'}
        </p>
      )}
    </section>
  );
}
